"use client";

import React, { useEffect } from "react";
import { useShopifyCookies } from "@shopify/hydrogen-react";
import { initializeEventTracking } from "./events/init";
import { AffiliateTracker } from "./affiliate";
import { createLogger, type LoggerConfig } from "./logger";
import {
  GoogleAdapter,
  PixelAdapter,
  MoengageAdapter,
  PostHogAdapter,
  ShopifyAdapter,
  KwikPassAdapter,
  KwikCheckoutAdapter,
} from "./adapters";
import type {
  GoogleAdapterConfig,
  PixelAdapterConfig,
  MoengageAdapterConfig,
  PostHogAdapterConfig,
  ShopifyAdapterConfig,
  KwikPassConfig,
  KwikCheckoutConfig,
} from "./adapters";
import type { TrackingAdapter } from "./events/subscriber";
import type { AffiliateConfig } from "./affiliate/types";

export interface ShopkitAnalyticsConfig {
  // Analytics Adapters
  googleAnalytics?: GoogleAdapterConfig;
  facebookPixel?: PixelAdapterConfig;
  moengage?: MoengageAdapterConfig;
  posthog?: PostHogAdapterConfig;
  shopify?: ShopifyAdapterConfig;
  kwikpass?: KwikPassConfig;
  kwikcheckout?: KwikCheckoutConfig;
  customAdapters?: TrackingAdapter[];

  // Affiliate Tracking
  affiliate?: {
    enabled?: boolean;
    config?: Partial<AffiliateConfig>;
    autoCapture?: boolean;
  };

  // Logger Settings
  logger?: LoggerConfig;

  // General Settings (deprecated - use logger.enabled instead)
  enableDebugLogs?: boolean;
  autoInitialize?: boolean;
}

export interface ShopkitAnalyticsProps {
  config: ShopkitAnalyticsConfig;
  children?: React.ReactNode;
  onInitialized?: () => void;
  onError?: (error: Error) => void;
}

const ShopkitAnalytics: React.FC<ShopkitAnalyticsProps> = ({
  config,
  children,
  onInitialized,
  onError,
}) => {
  // Enable Shopify cookies if Shopify adapter is configured
  useShopifyCookies({
    hasUserConsent: true,
    domain: config.shopify?.domain || "",
  });

  useEffect(() => {
    const initializeAnalytics = async () => {
      if (config.autoInitialize === false) return;

      // Create logger instance
      const loggerConfig: LoggerConfig = {
        enabled: config.logger?.enabled ?? config.enableDebugLogs ?? true,
        level: config.logger?.level ?? "info",
        name: "@shopkit/analytics",
        prettyPrint: config.logger?.prettyPrint ?? false,
      };

      const logger = createLogger(loggerConfig);

      try {
        logger.info("Starting initialization...");

        const adapters: TrackingAdapter[] = [];

        // Helper function to add logger config to adapter config
        const addLoggerConfig = (adapterConfig: any) => ({
          ...adapterConfig,
          enableDebugLogs: loggerConfig.enabled,
          logLevel: loggerConfig.level,
        });

        // Google Analytics 4
        if (config.googleAnalytics) {
          const gaAdapter = new GoogleAdapter(
            addLoggerConfig(config.googleAnalytics)
          );
          adapters.push(gaAdapter);
          logger.info("Added Google Analytics adapter");
        }

        // Facebook Pixel
        if (config.facebookPixel) {
          const pixelAdapter = new PixelAdapter(
            addLoggerConfig(config.facebookPixel)
          );
          adapters.push(pixelAdapter);
          logger.info("Added Facebook Pixel adapter");
        }

        // MoEngage
        if (config.moengage) {
          const moengageAdapter = new MoengageAdapter(
            addLoggerConfig(config.moengage)
          );
          adapters.push(moengageAdapter);
          logger.info("Added MoEngage adapter");
        }

        // PostHog
        if (config.posthog) {
          const posthogAdapter = new PostHogAdapter(
            addLoggerConfig(config.posthog)
          );
          adapters.push(posthogAdapter);
          logger.info("Added PostHog adapter");
        }

        // Shopify Analytics
        if (config.shopify) {
          const shopifyAdapter = new ShopifyAdapter(
            addLoggerConfig(config.shopify)
          );
          adapters.push(shopifyAdapter);
          logger.info("Added Shopify Analytics adapter");
        }

        // KwikPass Analytics
        if (config.kwikpass) {
          const kwikpassAdapter = new KwikPassAdapter(
            addLoggerConfig(config.kwikpass)
          );
          adapters.push(kwikpassAdapter);
          logger.info("Added KwikPass Analytics adapter");
        }

        // KwikCheckout Analytics
        if (config.kwikcheckout) {
          const kwikcheckoutAdapter = new KwikCheckoutAdapter(
            addLoggerConfig(config.kwikcheckout)
          );
          adapters.push(kwikcheckoutAdapter);
          logger.info("Added KwikCheckout Analytics adapter");
        }

        // Custom adapters
        if (config.customAdapters) {
          adapters.push(...config.customAdapters);
          logger.info(`Added ${config.customAdapters.length} custom adapters`);
        }

        if (adapters.length === 0) {
          logger.warn("No analytics adapters configured");
        } else {
          // Initialize the event tracking system
          await initializeEventTracking(adapters);
          logger.info("Event tracking initialized with adapters", {
            adapters: adapters.map((a) => a.name),
          });
        }

        logger.info("Initialization completed successfully");
        onInitialized?.();
      } catch (err) {
        const error =
          err instanceof Error
            ? err
            : new Error("Unknown initialization error");
        logger.error("Initialization failed", error);
        onError?.(error);
      }
    };

    initializeAnalytics();
  }, [config, onInitialized, onError]);

  return (
    <>
      {/* Affiliate Tracker */}
      {config.affiliate?.enabled !== false && (
        <AffiliateTracker
          config={config.affiliate?.config}
          autoCapture={config.affiliate?.autoCapture}
        />
      )}

      {children ? <>{children}</> : null}
    </>
  );
};

export default ShopkitAnalytics;
